'use client';

import { setupListeners } from '@reduxjs/toolkit/query';
import {
    Badge,
    Button,
    Label,
    ListGroup,
    ListGroupItem,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeadCell,
    TableRow,
    TextInput
} from 'flowbite-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiUserAdd, HiUserGroup } from 'react-icons/hi';
import { toast } from 'react-toastify';
import { QueryResult } from './Alerts.tsx';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import ProtectedRoute from './ProtectedRoute.tsx';
import { toastError } from './Toast.tsx';
import { useAppSelector, useAppStore } from '../lib/hooks.ts';
import {
    useBatchAanmeldenMutation,
    useLastYearUsersQuery,
    useCreateExternalUserMutation,
    useListExternalUsersQuery,
    useRevokeExternalUserMutation,
    useReinstateExternalUserMutation,
    ExternalUserSummary
} from '../lib/model/kantoordagApi.ts';
import { userHasRole } from '../lib/user-has-role.tsx';

interface OtherUserSignUpProps {
    signUpResult: ReturnType<typeof useBatchAanmeldenMutation>[1];
    email: string;
    setEmail: (email: string) => void;
}

export default function OtherUserSignUp({ signUpResult, email, setEmail }: OtherUserSignUpProps) {
    const { t } = useTranslation("signup");
    const user = useAppSelector(state => state.user.user)
    const store = useAppStore();

    // UI states
    const [openUserSelectionModal, setUserSelectionModal] = useState(false);
    const [createUserModal, setCreateUserModal] = useState(false);
    const [manageUsersModal, setManageUsersModal] = useState(false);

    // APIs
    const lastYearUsersQuery = useLastYearUsersQuery()

    setupListeners(store.dispatch)

    function externalUserCreated(userId: string) {
        setEmail(userId)
        signUpResult.reset()
    }

    function showCreateUserDialog() {
        setCreateUserModal(true)
        signUpResult.reset()
    }

    return (
        <ProtectedRoute authenticated={true}>
            <Modal show={openUserSelectionModal}
                   onClose={() => setUserSelectionModal(false)}
                   dismissible
            >
                <ModalHeader>{t("select_user_title")}</ModalHeader>
                <ModalBody className={"overflow-auto"}>
                    <QueryResult query={lastYearUsersQuery} message={t("last_year_users_failed")}>
                        {
                            lastYearUsersQuery.data !== undefined ?
                                lastYearUsersQuery.data!.users.length == 0 ?
                                    (
                                        <div>No users found</div>
                                    )
                                    :
                                    (
                                        <ListGroup>
                                            {
                                                lastYearUsersQuery.data!.users.map(user =>
                                                    <ListGroupItem key={user} onClick={() => setEmail(user)}>
                                                        {user}
                                                    </ListGroupItem>
                                                )
                                            }
                                        </ListGroup>
                                    )
                                : null
                        }
                    </QueryResult>
                </ModalBody>
            </Modal>

            <CreateExternalUserModal
                show={createUserModal}
                setShow={setCreateUserModal}
                userCreated={externalUserCreated}
            />

            <ManageExternalUsersModal
                show={manageUsersModal}
                setShow={setManageUsersModal}
            />

            {/* User selection, admin only */}
            {
                userHasRole(user, "ADMIN") ?
                    (
                        <div className="w-full flex-col">
                            <Label htmlFor={"email"}>{t('user-email')}</Label>
                            <div className="w-full flex flex-row">
                                <TextInput id={"email"}
                                           type={"email"}
                                           value={email}
                                           onChange={(e) => setEmail((e.target.value))}
                                           className="w-full"
                                />
                                <Button
                                    onClick={() => setUserSelectionModal(true)}
                                    color={"secondary"}
                                    outline={true}
                                >&hellip;</Button>
                                <Button
                                    onClick={showCreateUserDialog}
                                    color={"secondary"}
                                    outline={true}
                                >
                                    <HiUserAdd className="mr-2 h-5 w-5"/>
                                </Button>
                                <Button
                                    onClick={() => setManageUsersModal(true)}
                                    color={"secondary"}
                                    outline={true}
                                    title={t('manage_external_users_title')}
                                >
                                    <HiUserGroup className="mr-2 h-5 w-5"/>
                                </Button>
                            </div>
                        </div>
                    )
                    : null

            }
        </ProtectedRoute>
    );
}

interface CreateExternalUserModalProps {
    show: boolean
    setShow: (show: boolean) => void
    userCreated: (user: string) => void
}

function CreateExternalUserModal({ show, setShow, userCreated }: CreateExternalUserModalProps) {
    const { t } = useTranslation("signup");

    const [userEmail, setUserEmail] = useState("")
    const [userName, setUserName] = useState("")

    const [createUser, result] = useCreateExternalUserMutation()

    const isValid = userEmail.length != 0

    function createExternalUser() {
        createUser({
            externalUser: {
                userId: userEmail,
                userName: userName
            }
        }).unwrap().then(() => {
            toast.success(t("user_created"))
            userCreated(userEmail)
            setUserEmail("")
            setUserName("")
            setShow(false)
        }).catch(() => {
            toastError({ api: result, message: t("create_user_failed") })
        })
    }

    return (
        <Modal show={show}
               onClose={() => setShow(false)}
               dismissible
        >
            <ModalHeader>{t("create_external_user_title")}</ModalHeader>
            <ModalBody>
                <div className="w-full flex-col">
                    <label>{t('user-email')}</label>
                    <TextInput
                        type={"email"}
                        value={userEmail}
                        onChange={(e) => setUserEmail((e.target.value))}
                    />
                </div>
                <div className="w-full flex-col">
                    <label>{t('user-name')}</label>
                    <TextInput
                        type={"text"}
                        value={userName}
                        onChange={(e) => setUserName((e.target.value))}
                    />
                </div>
            </ModalBody>
            <ModalFooter>
                <Button onClick={createExternalUser} color={"primary"} disabled={!isValid}>{t('create')}</Button>
                <Button onClick={() => setShow(false)}>{t('cancel')}</Button>
            </ModalFooter>
        </Modal>
    )
}

interface ManageExternalUsersModalProps {
    show: boolean
    setShow: (show: boolean) => void
}

function ManageExternalUsersModal({ show, setShow }: ManageExternalUsersModalProps) {
    const { t } = useTranslation("signup");

    const [userToRevoke, setUserToRevoke] = useState<ExternalUserSummary | null>(null)

    const listQuery = useListExternalUsersQuery(undefined, { skip: !show })
    const [revokeUserApi, revokeResult] = useRevokeExternalUserMutation()
    const [reinstateUserApi, reinstateResult] = useReinstateExternalUserMutation()

    function askRevoke(user: ExternalUserSummary) {
        setUserToRevoke(user)
    }

    function cancelRevoke() {
        setUserToRevoke(null)
    }

    function confirmRevoke() {
        const user = userToRevoke
        setUserToRevoke(null)
        if (user === null) {
            return
        }
        revokeUserApi({ id: user.id }).unwrap().then(() => {
            toast.success(t("revoke_external_user_success"))
        }).catch(() => {
            toastError({ api: revokeResult, message: t("revoke_external_user_failed") })
        })
    }

    function reinstate(user: ExternalUserSummary) {
        reinstateUserApi({ id: user.id }).unwrap().then(() => {
            toast.success(t("reinstate_external_user_success"))
        }).catch(() => {
            toastError({
                api: reinstateResult,
                message: t("reinstate_external_user_failed"),
                errorMapper: (status) => status === 409 ? t("reinstate_external_user_never_redeemed") : null
            })
        })
    }

    return (
        <Modal show={show}
               onClose={() => setShow(false)}
               dismissible
               size={"4xl"}
        >
            <ModalHeader>{t("manage_external_users_title")}</ModalHeader>
            <ModalBody className={"overflow-auto"}>
                <ConfirmDialog
                    message={t("confirm_revoke_external_user", { name: userToRevoke?.displayName ?? "" })}
                    yes={t('yes')}
                    no={t('no')}
                    show={userToRevoke !== null}
                    onYes={confirmRevoke}
                    onNo={cancelRevoke}
                />
                <QueryResult query={listQuery} message={t("list_external_users_failed")}>
                    {
                        listQuery.data !== undefined ?
                            listQuery.data.length === 0 ?
                                (
                                    <div>{t("no_external_users")}</div>
                                )
                                :
                                (
                                    <Table>
                                        <TableHead>
                                            <TableHeadCell>{t('user-name')}</TableHeadCell>
                                            <TableHeadCell>{t('user-email')}</TableHeadCell>
                                            <TableHeadCell>{t('invitation_state')}</TableHeadCell>
                                            <TableHeadCell>{t('access_state')}</TableHeadCell>
                                            <TableHeadCell/>
                                        </TableHead>
                                        <TableBody>
                                            {
                                                listQuery.data.map(externalUser => (
                                                    <TableRow key={externalUser.id}>
                                                        <TableCell>{externalUser.displayName}</TableCell>
                                                        <TableCell>{externalUser.email}</TableCell>
                                                        <TableCell>
                                                            <Badge color={externalUser.invitationState === "Accepted" ? "success" : "warning"}
                                                                   className={"w-fit"}
                                                            >
                                                                {
                                                                    externalUser.invitationState === "Accepted" ?
                                                                        t("invitation_accepted")
                                                                        : t("invitation_pending")
                                                                }
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge color={externalUser.revoked ? "failure" : "success"} className={"w-fit"}>
                                                                {externalUser.revoked ? t("revoked") : t("active")}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            {
                                                                externalUser.revoked ?
                                                                    <Button size={"xs"}
                                                                            color={"primary"}
                                                                            onClick={() => reinstate(externalUser)}
                                                                    >
                                                                        {t("reinstate")}
                                                                    </Button>
                                                                    :
                                                                    <Button size={"xs"}
                                                                            color={"secondary"}
                                                                            onClick={() => askRevoke(externalUser)}
                                                                    >
                                                                        {t("revoke")}
                                                                    </Button>
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            }
                                        </TableBody>
                                    </Table>
                                )
                            : null
                    }
                </QueryResult>
            </ModalBody>
            <ModalFooter>
                <Button onClick={() => setShow(false)}>{t('cancel')}</Button>
            </ModalFooter>
        </Modal>
    )
}